/**
 * The receipt, drawn — on the letterhead.
 *
 * A PDF for the same reason the invoice is one (ADR 0068): it is a thing the
 * customer keeps, and the email it arrives on is only the envelope. Drawn in
 * the browser for the same reason too — nothing on the server can make a PDF.
 *
 * The letterhead is the full wordmark, centred, over the company's address,
 * phone and website and a copper rule. The light-background wordmark, because
 * a receipt is a white sheet: on it the black half of the lockup is the half
 * that reads "RIDGE ROOFING", which is the company's own name (CLAUDE.md rule
 * 11). No licence number — nothing a customer reads prints one.
 *
 * Nothing here computes money. Every figure arrives on the snapshot, worked
 * out by `buildReceipt` in packages/core.
 */

import {
  invoiceMoney,
  receiptDate,
  type ReceiptSnapshot,
} from "@roof-hawk/core";

import { WORDMARK_ON_LIGHT } from "../logo";

/** Letter, in points, which is what jsPDF measures in. */
const PAGE = { width: 612, height: 792 };
const MARGIN = 54;
const RIGHT = PAGE.width - MARGIN;
const CENTRE = PAGE.width / 2;

const INK = "#1a1a1a";
const GREY = "#6b7280";
const RULE = "#d4d4d8";
/** The copper of the wordmark, for the rule and the stamp. */
const COPPER = "#b8652f";

/** The wordmark file is 640 × 350. */
const MARK = { width: 160, height: 160 * (350 / 640) };

type Doc = import("jspdf").jsPDF;

/**
 * The wordmark, as bytes a PDF can hold.
 *
 * Fetched rather than imported, like the invoice's badge: it changes when the
 * company rebrands. A failure to load it is not a failure to receipt — the
 * company's name is printed in type underneath either way.
 */
async function wordmarkDataUrl(
  url = WORDMARK_ON_LIGHT,
): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** A label in the small grey capitals the headings use. */
function heading(
  doc: Doc,
  text: string,
  x: number,
  y: number,
  align?: "right",
): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(GREY);
  doc.text(text.toUpperCase(), x, y, align ? { align } : undefined);
  doc.setTextColor(INK);
}

function rule(
  doc: Doc,
  y: number,
  from = MARGIN,
  to = RIGHT,
  colour = RULE,
  width = 0.7,
): void {
  doc.setDrawColor(colour);
  doc.setLineWidth(width);
  doc.line(from, y, to, y);
}

function lines(
  doc: Doc,
  text: readonly string[],
  x: number,
  top: number,
): void {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  text.forEach((line, at) => doc.text(line, x, top + at * 15));
}

/**
 * Draw the whole thing.
 *
 * Exported for the test, which reads the PDF back rather than looking at it.
 */
export async function renderReceipt(
  receipt: ReceiptSnapshot,
  options: { logo?: string | null } = {},
): Promise<Doc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    // Uncompressed, so a test can read the words back out of the file.
    compress: false,
  });

  doc.setProperties({
    title: `Receipt ${receipt.number}`,
    author: receipt.company.name,
    subject: `Receipt ${receipt.number}`,
  });

  // --------------------------------------------------------- letterhead ---

  const logo =
    options.logo === undefined ? await wordmarkDataUrl() : options.logo;
  let y = 40;
  if (logo) {
    try {
      doc.addImage(
        logo,
        "PNG",
        CENTRE - MARK.width / 2,
        y,
        MARK.width,
        MARK.height,
      );
      y += MARK.height + 16;
    } catch {
      y += 8;
    }
  }
  if (!logo) {
    // No picture: the name in type, so the sheet is never anonymous.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(INK);
    doc.text(receipt.company.name, CENTRE, y + 20, { align: "center" });
    y += 40;
  }

  const contact = [
    receipt.company.address,
    receipt.company.phone,
    receipt.company.website,
  ].filter(Boolean) as string[];
  if (contact.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(GREY);
    doc.text(contact.join("   |   "), CENTRE, y, { align: "center" });
    y += 14;
  }
  rule(doc, y, MARGIN, RIGHT, COPPER, 1.6);

  // -------------------------------------------------------------- title ---

  const titleY = y + 62;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(28);
  doc.setTextColor(INK);
  doc.text("Payment Receipt", MARGIN, titleY);

  const meta: [string, string][] = [
    ["Receipt no.", receipt.number],
    ["Date", receiptDate(receipt.receivedOn)],
    ["Job no.", receipt.jobNumber],
  ];
  meta.forEach(([label, value], at) => {
    const rowY = titleY - 30 + at * 15;
    doc.setFontSize(10);
    doc.setTextColor(GREY);
    doc.text(label, RIGHT - 170, rowY);
    doc.setTextColor(INK);
    doc.text(value, RIGHT, rowY, { align: "right" });
  });

  // ------------------------------------------------------------ parties ---

  const partiesY = titleY + 44;
  const second = CENTRE + 12;
  heading(doc, "Received from", MARGIN, partiesY);
  heading(doc, "Received by", second, partiesY);
  rule(doc, partiesY + 7, MARGIN, CENTRE - 12);
  rule(doc, partiesY + 7, second, RIGHT);
  lines(
    doc,
    [receipt.customer.name, ...receipt.customer.addressLines],
    MARGIN,
    partiesY + 26,
  );
  lines(
    doc,
    [receipt.company.name, ...receipt.company.addressLines],
    second,
    partiesY + 26,
  );

  // -------------------------------------------------------------- items ---

  const tableY = partiesY + 104;
  const methodX = 360;
  heading(doc, "Description", MARGIN, tableY);
  heading(doc, "Payment method", methodX, tableY);
  heading(doc, "Amount", RIGHT, tableY, "right");
  rule(doc, tableY + 7);

  const rowY = tableY + 28;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(INK);
  doc.text(receipt.description, MARGIN, rowY);
  doc.text(receipt.method, methodX, rowY);
  doc.text(invoiceMoney(receipt.amount), RIGHT, rowY, { align: "right" });
  let below = rowY;
  if (receipt.property) {
    doc.setFontSize(9);
    doc.setTextColor(GREY);
    doc.text(receipt.property, MARGIN, rowY + 14);
    doc.setTextColor(INK);
    below += 14;
  }
  rule(doc, below + 14);

  // --------------------------------------------------------------- sums ---

  const sums: [string, string][] = [];
  if (receipt.contract != null) {
    sums.push(["Job total", invoiceMoney(receipt.contract)]);
  }
  sums.push(["This payment", invoiceMoney(receipt.amount)]);

  let sumY = below + 42;
  const sumsX = RIGHT - 220;
  doc.setFontSize(10.5);
  for (const [label, value] of sums) {
    doc.text(label, sumsX, sumY);
    doc.text(value, RIGHT, sumY, { align: "right" });
    sumY += 18;
  }
  if (receipt.balanceAfter != null) {
    rule(doc, sumY - 6, sumsX, RIGHT, INK, 1.4);
    doc.setFontSize(15);
    doc.text("Balance due", sumsX, sumY + 14);
    doc.text(invoiceMoney(receipt.balanceAfter), RIGHT, sumY + 14, {
      align: "right",
    });
    sumY += 30;
  }

  if (receipt.paidInFull) {
    const stampY = sumY + 30;
    const width = 150;
    doc.setDrawColor(COPPER);
    doc.setLineWidth(1.6);
    doc.rect(CENTRE - width / 2, stampY, width, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(COPPER);
    doc.text("PAID IN FULL", CENTRE, stampY + 19.5, {
      align: "center",
      charSpace: 2,
    });
    doc.setTextColor(INK);
    sumY = stampY + 30;
  }

  // ------------------------------------------------------------- footer ---

  const footY = Math.max(sumY + 60, PAGE.height - 150);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(INK);
  doc.text(
    `Thank you for your payment, and for choosing ${receipt.company.name}.`,
    MARGIN,
    footY,
  );
  if (receipt.contact) {
    const reach = [
      receipt.contact.name,
      receipt.contact.phone,
      receipt.contact.email,
    ]
      .filter(Boolean)
      .join(", ");
    doc.text(`Questions about this receipt: ${reach}`, MARGIN, footY + 16);
  }

  return doc;
}

/** The file as base64, which is what the Gmail Edge Function attaches. */
export async function receiptPdfBase64(
  receipt: ReceiptSnapshot,
  options: { logo?: string | null } = {},
): Promise<string> {
  const doc = await renderReceipt(receipt, options);
  return doc.output("datauristring").split(",")[1] ?? "";
}
